// Contract of the Power admin search by id (Operações): the operator pastes an x-fapi-interaction-id
// or a DLQ event id (a UUID) or a W3C trace id, and gets one timeline of what the platform kept
// under it: the OFB and OPIN PCM calls and the parked DLQ events, oldest first, how each source
// answered, and a link to the logs and the trace in Grafana. Power never reads Loki or Tempo; the
// link is all it gives of them. Every free text a hit carries was scrubbed by Power, and a PCM
// call's record is answered as a redacted view.
//
// The structs are closed contracts: decode them with `{ onExcessProperty: 'error' }`.
import { Schema } from 'effect';
import { IsoDateTimeUtc } from '@finnest/core/effect';
import { DlqInboxItem, RedactedView } from './dlq.ts';

/** The longest id the search reads; anything longer is refused before it is normalized. */
export const SEARCH_ID_MAX = 128;

/** The `code` the 400 names, in its `errors` entry, for an id that is neither a UUID nor a trace id. */
export const SEARCH_ID_INVALID = 'search-id-invalid';

/** The query: the id as the operator typed it; Power normalizes it, or refuses it with 400. */
export const OperationsSearchQuery = Schema.Struct({
  id: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(SEARCH_ID_MAX)),
});
export type OperationsSearchQuery = typeof OperationsSearchQuery.Type;

/**
 * What the id was read as: a UUID (lowercase; an interaction id or a DLQ event id) or a trace id
 * (32 lowercase hex digits; one of 31 gets its leading zero back).
 */
export const SearchIdKind = Schema.Literals(['uuid', 'trace']);
export type SearchIdKind = typeof SearchIdKind.Type;

/** The PCM call trails the search reads: OFB on the banking service, OPIN on the insurance one. */
export const PcmSearchSource = Schema.Literals(['pcm-ofb', 'pcm-opin']);
export type PcmSearchSource = typeof PcmSearchSource.Type;

/** Every source the search reads. */
export const SearchSource = Schema.Literals(['pcm-ofb', 'pcm-opin', 'dlq']);
export type SearchSource = typeof SearchSource.Type;

/**
 * How one source answered: in time (`ok`, its hits are in the timeline), failed or refused the
 * read (`unavailable`), or did not answer within its 2.5 s limit (`timeout`).
 */
export const SearchSourceState = Schema.Literals(['ok', 'unavailable', 'timeout']);
export type SearchSourceState = typeof SearchSourceState.Type;

/** The platform's role in a PCM call, as the PCM names it: the server answered, the client sent. */
export const PcmCallRole = Schema.Literals(['SERVER', 'CLIENT']);
export type PcmCallRole = typeof PcmCallRole.Type;

const Text = Schema.NonEmptyString;
const INTERACTION_ID_MAX = 100;

/**
 * One hit: a PCM call (`outcome` is where its report stands in the source's own words, the OFB
 * capture state or batch status, the OPIN report state; `detail` is the call's whole record as a
 * redacted view) or a parked DLQ event, as the DLQ inbox shows it.
 */
export const SearchHit = Schema.TaggedUnion({
  PcmCall: {
    source: PcmSearchSource,
    occurredAt: IsoDateTimeUtc,
    endpoint: Text,
    method: Text,
    statusCode: Schema.NullOr(Schema.Int),
    outcome: Text,
    role: PcmCallRole,
    interactionId: Schema.String.check(
      Schema.isMinLength(1),
      Schema.isMaxLength(INTERACTION_ID_MAX),
    ),
    detail: RedactedView,
  },
  DlqEvent: { item: DlqInboxItem },
}).annotate({
  title: 'PowerAdminSearchHit',
  description: 'A PCM call or a parked DLQ event found under the searched id.',
});
export type SearchHit = typeof SearchHit.Type;

/** How one source answered the search. */
export const SearchSourceStatus = Schema.Struct({
  source: SearchSource,
  state: SearchSourceState,
});
export type SearchSourceStatus = typeof SearchSourceStatus.Type;

/**
 * Where the logs and the trace are: the `finnest-client-requests` Grafana dashboard around the
 * hits, or nothing, when the deployment configures no Grafana.
 */
export const SearchLogsLink = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal('grafana'),
    url: Schema.String.check(Schema.isMinLength(1)),
  }),
  Schema.Struct({ kind: Schema.Literal('not-configured') }),
]);
export type SearchLogsLink = typeof SearchLogsLink.Type;

/**
 * The search's answer: the id as it was searched, the hits oldest first, each source the id was
 * searched in and how it answered (a UUID in the PCM trails the deployment serves and the DLQ, a
 * trace id in the DLQ only), and the logs link.
 */
export const OperationsSearchResult = Schema.Struct({
  normalizedId: Schema.String,
  kind: SearchIdKind,
  hits: Schema.Array(SearchHit),
  sources: Schema.Array(SearchSourceStatus),
  logs: SearchLogsLink,
}).annotate({
  title: 'PowerAdminOperationsSearchResult',
  description: 'The calls and parked events kept under one id, oldest first, and their sources.',
});
export type OperationsSearchResult = typeof OperationsSearchResult.Type;
